import React, {
  useState,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { node } from "prop-types";
import {
  applyProfileOverlay,
  getToken,
  getUserFromLocalStorage,
  saveProfileOverlay,
  setTokenInLocalStorage,
  removeToken,
} from "../service/localStorage";
import { syncSupabaseToApp } from "../service/userApi";
import { supabase, isSupabaseConfigured } from "../../lib/supabaseClient";
import { claimRefusals } from "../../utils/refusalNotice";

const UserContext = React.createContext(null);

/**
 * Upper bound on how long the Supabase session lookup may hold the whole app
 * behind `authReady` (#2).
 *
 * `authReady === false` is not a loading state anywhere — RequireAuth,
 * RequireAdmin and Router's RootRedirect all `return null` while it is false,
 * so a lookup that never settles paints a blank shell forever, with no spinner
 * and nothing to retry. On native it is worse than a hang: SPLASH_FAILSAFE_MS
 * (4s, see src/native/splashScreen.js) drops the splash regardless, so the user
 * is delivered onto that blank shell rather than left on a branded screen.
 *
 * `getSession()` awaits a token refresh over the network on the default fetch,
 * with no timeout of its own, so "never settles" is a real outcome and not a
 * theoretical one.
 *
 * The bound is a compromise between two costs, and is deliberately generous in
 * the same spirit as MAP_LOAD_TIMEOUT_MS — a backstop for a session that is not
 * coming, not a performance budget:
 *
 * - Too short and a slow-but-alive cold start gets called signed-out and
 *   bounced to /login while its session was still in flight. 4s (matching the
 *   splash) would do that to real users on a bad mobile connection.
 * - Too long and every millisecond past the 4s splash drop is a millisecond of
 *   blank screen. 15s would be eleven seconds of nothing.
 *
 * What makes 10s affordable rather than merely arbitrary is that firing it is
 * recoverable: a session that arrives late still bridges (see the `.then`
 * below), `user` goes non-null, and LoginPage's `if (user) return <Navigate
 * to={from} />` carries the user on to where they were originally headed. The
 * deadline costs a late arrival a flash of the login page, never their session.
 */
export const AUTH_READY_TIMEOUT_MS = 10000;

export const UserProvider = ({ children }) => {
  // Read synchronously so a signed-in user is never null on the first
  // render — RequireAuth otherwise treats that transient null as "signed
  // out" and bounces straight to /login before this ever runs.
  const [user, setUser] = useState(getUserFromLocalStorage);
  const [token, setToken] = useState(getToken);
  // The local-storage case above resolves in the same tick. Only the
  // Supabase session bridge below is genuinely asynchronous, so start
  // "ready" unless that bridge still has to run.
  const [authReady, setAuthReady] = useState(
    () => !isSupabaseConfigured() || !!getToken()
  );
  const bridgingRef = useRef(false);

  const bridgeSession = async (accessToken) => {
    if (bridgingRef.current) return;
    bridgingRef.current = true;
    try {
      const appToken = await syncSupabaseToApp(accessToken);
      setTokenInLocalStorage(appToken);
      setToken(appToken);
      setUser(getUserFromLocalStorage());
    } catch (err) {
      // A background bridge: nobody asked, so a refusal is not toasted (B-31).
      claimRefusals();
      console.warn("[UserProvider] Supabase session bridge failed:", err);
    } finally {
      bridgingRef.current = false;
    }
  };

  /**
   * Adopt a profile the user just saved (P1-38).
   *
   * `user` is decoded from the app JWT, and the profile endpoint does not
   * re-mint that token — so a save that only updated React state was undone by
   * the next reload, and a save that only wrote storage left the avatar on
   * screen stale. Both halves go through here, off one normalized overlay, so
   * they cannot disagree.
   *
   * Takes { _id, name, image } — the fields the token carries and a profile
   * save can change. Everything else about the user stays the token's.
   */
  const applyProfileUpdate = useCallback((profile) => {
    const overlay = saveProfileOverlay(profile);
    if (!overlay) return;
    setUser((prev) => applyProfileOverlay(prev, overlay));
  }, []);

  useEffect(() => {
    if (!isSupabaseConfigured() || !supabase) return;

    // `authReady` has three racers below — the lookup resolving, the lookup
    // rejecting, and the deadline elapsing — and exactly one of them may flip
    // it. A one-way latch, rather than three independent `setAuthReady(true)`
    // calls, because the interesting case is a lookup that resolves *after* the
    // deadline already fired: that one must not touch the flag again, and must
    // not touch state at all if the provider is gone by then.
    let cancelled = false; // set once, by cleanup: this provider is unmounted
    let settled = false; // set once, by whichever racer got there first
    let readyTimer = null;

    const markReady = () => {
      if (settled || cancelled) return;
      settled = true;
      window.clearTimeout(readyTimer);
      readyTimer = null;
      setAuthReady(true);
    };

    // Initial bridge: if we have a Supabase session but no app token
    if (!getToken()) {
      readyTimer = window.setTimeout(markReady, AUTH_READY_TIMEOUT_MS);
      supabase.auth
        .getSession()
        .then((result) => {
          if (cancelled) return;
          // Optional chain, not `({ data: { session } })`: a destructure of a
          // missing envelope throws *inside* this callback, which used to mean
          // authReady simply never flipped. Defensive — no auth-js path is
          // known to resolve without `data` — but the failure mode it guards
          // against is the whole app, so it is cheap insurance.
          const session = result?.data?.session;
          if (session?.access_token) {
            // Deliberately still bridges after the deadline: see
            // AUTH_READY_TIMEOUT_MS. `markReady` is a no-op by then, but the
            // sign-in itself is what makes the deadline recoverable.
            bridgeSession(session.access_token).finally(markReady);
          } else {
            markReady();
          }
        })
        .catch((err) => {
          // A rejection here means "we could not find out whether there is a
          // session", which resolves to the same place a lookup that found
          // nothing does: signed out. That is exactly what authReady has always
          // meant — "auth has resolved as far as it is going to" — so no
          // consumer contract moves.
          console.warn("[UserProvider] Supabase getSession failed:", err);
          markReady();
        });
    }

    // Listen for auth state changes (token refresh, sign-in, sign-out)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === "SIGNED_OUT") {
          removeToken();
          setToken(null);
          setUser(null);
          return;
        }
        if (!session?.access_token) return;

        // TOKEN_REFRESHED bridges unconditionally. The app JWT is independent
        // of the Supabase one — the server verifies only its own, on its own
        // ~8h clock — and nothing else re-mints it while the app stays open,
        // so this is the sole renewal event on a long-lived session. Guarding
        // it on "already signed in" would let a live token quietly age out and
        // sign the user out mid-use. It costs nothing either: auth-js only
        // refreshes within 90s of expiry on a ~1h token, so ~once an hour.
        if (event === "TOKEN_REFRESHED") {
          bridgeSession(session.access_token);
          return;
        }

        // SIGNED_IN is a FOREGROUND event, not a sign-in event (#40). auth-js
        // re-notifies it on every `visibilitychange` for any stored session,
        // with no dedupe, so an unguarded bridge here turned each app-switch
        // into a POST /users/supabase-session — ten of which exhaust the
        // authLimiter bucket that /users/login shares, locking the user out of
        // signing in for 15 minutes with no password ever typed.
        //
        // So it only bridges when there is nothing to bridge *to*, matching the
        // initial bridge above. Safe because no real sign-in relies on this
        // listener: the password path and the OAuth callback both bridge
        // explicitly, and logout clears the app token before signOut(), so a
        // fresh sign-in never sees a stale one here.
        //
        // `getUserFromLocalStorage()`, not `getToken()`: the latter is a raw
        // string read with no expiry check, so an expired-but-present token
        // would skip the bridge exactly when it is needed. This one decodes,
        // checks `exp`, and drops a dead token as it goes.
        if (event === "SIGNED_IN" && !getUserFromLocalStorage()) {
          bridgeSession(session.access_token);
        }
      }
    );

    return () => {
      cancelled = true;
      window.clearTimeout(readyTimer);
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => {
    return { user, setUser, applyProfileUpdate, token, setToken, authReady };
  }, [user, token, authReady, applyProfileUpdate]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) throw new Error("useUser must be used within a UserProvider");
  return context;
};
UserProvider.propTypes = {
  children: node.isRequired,
};
